import { useEffect } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Cloud,
  CloudOff,
  RefreshCw,
} from "lucide-react";
import { useSyncStore } from "@/stores/syncStore";
import { cn } from "@/lib/utils";

export function SyncStatusIndicator() {
  const {
    isOnline,
    isSyncing,
    pendingChanges,
    failedCount,
    lastSyncAt,
    initializeSync,
    triggerSync,
    retryFailed,
  } = useSyncStore();

  // Initialize sync on mount
  useEffect(() => {
    const cleanup = initializeSync();
    return cleanup;
  }, [initializeSync]);

  // Format last sync time
  const formatLastSync = () => {
    if (!lastSyncAt) return "Never";
    const now = new Date();
    const diff = now.getTime() - lastSyncAt.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return lastSyncAt.toLocaleDateString();
  };

  // Determine status
  const getStatus = () => {
    if (!isOnline) return "offline";
    if (isSyncing) return "syncing";
    if (failedCount > 0) return "error";
    if (pendingChanges > 0) return "pending";
    return "synced";
  };

  const status = getStatus();
  const lastSyncLabel = formatLastSync();
  const isActionable =
    isOnline && !isSyncing && (pendingChanges > 0 || failedCount > 0);
  const statusConfig = {
    offline: {
      Icon: CloudOff,
      label: "Offline",
      detail: "Changes will sync when the connection returns",
      className: "bg-gray-100 text-gray-500 hover:bg-gray-200",
    },
    syncing: {
      Icon: RefreshCw,
      label: "Syncing",
      detail: "Sync in progress",
      className: "bg-blue-50 text-blue-600",
    },
    error: {
      Icon: AlertCircle,
      label: `${failedCount} sync ${failedCount === 1 ? "failure" : "failures"}`,
      detail: "Click to retry failed changes",
      className: "bg-red-50 text-red-600 hover:bg-red-100",
    },
    pending: {
      Icon: Cloud,
      label: `${pendingChanges} pending ${pendingChanges === 1 ? "change" : "changes"}`,
      detail: "Click to sync now",
      className: "bg-amber-50 text-amber-600 hover:bg-amber-100",
    },
    synced: {
      Icon: CheckCircle2,
      label: "Synced",
      detail: `Last synced ${lastSyncLabel.toLowerCase()}`,
      className: "bg-emerald-50 text-emerald-600",
    },
  }[status];
  const StatusIcon = statusConfig.Icon;
  const statusContents = (
    <>
      <StatusIcon
        className={`h-[18px] w-[18px] ${status === "syncing" ? "animate-spin" : ""}`}
      />
      {(status === "error" || status === "pending") && (
        <span
          className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white px-0.5 text-[9px] font-semibold text-white ${status === "error" ? "bg-red-500" : "bg-amber-500"}`}
        >
          {status === "error"
            ? Math.min(failedCount, 9)
            : Math.min(pendingChanges, 9)}
        </span>
      )}
    </>
  );

  return (
    <div title={`${statusConfig.label} — ${statusConfig.detail}`}>
      {isActionable ? (
        <button
          type="button"
          onClick={() => (failedCount > 0 ? retryFailed() : triggerSync())}
          aria-label={`${statusConfig.label}. ${statusConfig.detail}`}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500",
            statusConfig.className,
          )}
        >
          {statusContents}
        </button>
      ) : (
        <div
          role="status"
          aria-label={`${statusConfig.label}. ${statusConfig.detail}`}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-xl",
            statusConfig.className,
          )}
        >
          {statusContents}
        </div>
      )}
    </div>
  );
}

// Compact version for header
export function SyncStatusBadge() {
  const { isOnline, isSyncing, pendingChanges, failedCount } = useSyncStore();

  if (!isOnline) {
    return (
      <div className="flex items-center gap-1 text-gray-500">
        <CloudOff className="w-4 h-4" />
        <span className="text-xs">Offline</span>
      </div>
    );
  }

  if (isSyncing) {
    return (
      <div className="flex items-center gap-1 text-blue-500">
        <RefreshCw className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (failedCount > 0) {
    return (
      <div className="flex items-center gap-1 text-red-500">
        <AlertCircle className="w-4 h-4" />
        <span className="text-xs">{failedCount}</span>
      </div>
    );
  }

  if (pendingChanges > 0) {
    return (
      <div className="flex items-center gap-1 text-yellow-500">
        <Cloud className="w-4 h-4" />
        <span className="text-xs">{pendingChanges}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center text-green-500">
      <Cloud className="w-4 h-4" />
    </div>
  );
}
